import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils";

/**
 * Button
 *
 * The single interactive button primitive. Monochrome by design: `primary`
 * inverts to a white fill, everything else stays on the black canvas and is
 * distinguished by border and hover wash alone.
 *
 * @example
 *   <Button variant="primary" onClick={save}>Save</Button>
 *   <Button variant="ghost" size="sm" iconLeft={<Plus size={14} />}>Add</Button>
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner and disable interaction. */
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md " +
  "font-medium transition-[background-color,border-color,color,transform] duration-fast ease-standard select-none " +
  "active:scale-[0.97] active:duration-instant " +
  "disabled:pointer-events-none disabled:opacity-40 focus-visible:outline " +
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-black border border-accent hover:bg-white/90 active:bg-white/80",
  secondary:
    "bg-bg-elevated text-text-primary border border-border hover:bg-bg-hover " +
    "hover:border-border-strong active:bg-bg-active",
  ghost:
    "bg-transparent text-text-secondary border border-transparent " +
    "hover:bg-bg-hover hover:text-text-primary active:bg-bg-active",
  danger:
    "bg-transparent text-text-primary border border-border-strong " +
    "hover:bg-bg-hover active:bg-bg-active",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-sm",
  md: "h-8 px-3 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "secondary",
      size = "md",
      loading = false,
      iconLeft,
      iconRight,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin" aria-hidden />
      ) : (
        iconLeft
      )}
      {children}
      {!loading && iconRight}
    </button>
  ),
);

Button.displayName = "Button";
